import type { TextVariants } from "@/components/ui/baseUi/text"
import { Text } from "@/components/ui/baseUi/text"
import { Language } from "@/modules/language/utils/languageUtils"
import { cn } from "@/modules/lib/utils"
import appIcon from "@images/appIcon.svg"
import { privacyPolicyData, type PrivacyPolicyTermsType } from "@modules/consumerAgreements/datas/privacyPolicy.data"
import { useLanguage } from "@modules/language/hooks/useLanguage"
import type { VariantProps } from "class-variance-authority"

type PrivacyPolicyPageProps = {
  className?: string
} & React.ComponentProps<"div">
export function PrivacyPolicyPage({ className, ...props }: PrivacyPolicyPageProps) {
  const language = useLanguage() === Language.FRENCH ? Language.FRENCH : Language.ENGLISH_US

  const formatTerms = (terms: PrivacyPolicyTermsType[], parentIndex: string = "0") => {
    return terms.map((term, termIndex) => (
      <div key={termIndex} className={cn("flex flex-col items-start gap-5", className)} {...props}>
        <div className="flex flex-row gap-2">
          <Text fontStyle="LL">{`${parentIndex === "0" ? "" : parentIndex + "."}${term.index}.`}</Text>
          <div className="flex flex-col gap-5 [&_a]:break-all [&_a]:text-purple-500">
            {term.title && (
              <Text fontStyle="LL" className={parentIndex === "0" ? "underline" : ""}>
                {term.title}
              </Text>
            )}
            {term.content && formatTextIndentation(term.content, "BM")}
          </div>
        </div>

        {term.table && (
          <table className="w-full table-fixed border-collapse border border-black p-1">
            <thead>
              <tr>
                {term.table.headers.map((header, headerIndex) => (
                  <th key={"header-" + headerIndex} className="w-1/3 border-collapse border border-black p-1">
                    {header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {term.table.rows.map((row, rowIndex) => (
                <tr key={"row-" + rowIndex}>
                  {row.map((cell, cellIndex) => (
                    <td key={"cell-" + cellIndex} className="w-1/3 border-collapse border border-black p-1">
                      {cell.split("\n").map((cellLine, cellLineIndex) => (
                        <span key={cellLineIndex}>
                          <Text fontStyle="BM">{cellLine}</Text>
                          <br />
                        </span>
                      ))}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    ))
  }

  const formatTextIndentation = (
    text: string,
    fontStyle: VariantProps<typeof TextVariants>["fontStyle"],
    className?: string,
  ) => {
    return text.split("\n").map((line, lineIndex) => (
      <Text key={lineIndex} fontStyle={fontStyle} className={cn("text-justify leading-normal!", className)}>
        <div dangerouslySetInnerHTML={{ __html: line }} />
      </Text>
    ))
  }

  return (
    <div className="m-auto flex max-w-[960px] flex-col gap-2 px-7.5 py-12.5">
      <div className="flex flex-col gap-5">
        <div className="flex flex-col items-center gap-2 underline">
          {formatTextIndentation(privacyPolicyData[language].title, "HM", "text-center")}
          <img src={appIcon} alt="appIcon" className="h-25 w-25" />
        </div>

        {formatTerms(privacyPolicyData[language].terms)}

        <div className="flex flex-row items-center gap-5">
          {formatTextIndentation(privacyPolicyData[language].entryIntoForceLabel, "LL")}
          {formatTextIndentation(privacyPolicyData[language].entryIntoForceValue, "BM")}
        </div>
      </div>
    </div>
  )
}
